import * as zarr from "zarrita";

import { ZarrDataManager } from "./ZarrDataManager.ts";
import {
  getCRSStringForXYVariable,
  getLatLonVariableInfo,
  isLatitudeName,
  isLongitudeName,
  isPolarStereographicCRS,
  isXName,
  isYName,
} from "./zarrUtils.ts";

import type { TSources } from "@/lib/types/GlobeTypes.ts";

export const GRID_TYPES = {
  REGULAR: "regular",
  HEALPIX: "healpix",
  REGULAR_ROTATED: "regular_rotated",
  TRIANGULAR: "triangular",
  GAUSSIAN_REDUCED: "gaussian_reduced",
  IRREGULAR: "irregular",
  IRREGULAR_DELAUNAY: "irregular_delaunay",
  CURVILINEAR: "curvilinear",
  ERROR: "error",
} as const;

export type T_GRID_TYPES = (typeof GRID_TYPES)[keyof typeof GRID_TYPES];

/* Some grid types can be displayed as others. Maps to array of alternatives. */
export const GRID_TYPE_DISPLAY_OVERRIDES: Partial<
  Record<T_GRID_TYPES, T_GRID_TYPES[]>
> = {
  [GRID_TYPES.REGULAR]: [GRID_TYPES.IRREGULAR, GRID_TYPES.IRREGULAR_DELAUNAY],
  [GRID_TYPES.REGULAR_ROTATED]: [
    GRID_TYPES.IRREGULAR,
    GRID_TYPES.IRREGULAR_DELAUNAY,
  ],
  [GRID_TYPES.CURVILINEAR]: [
    GRID_TYPES.IRREGULAR,
    GRID_TYPES.IRREGULAR_DELAUNAY,
  ],
  [GRID_TYPES.GAUSSIAN_REDUCED]: [
    GRID_TYPES.IRREGULAR,
    GRID_TYPES.IRREGULAR_DELAUNAY,
  ],
  [GRID_TYPES.IRREGULAR]: [GRID_TYPES.IRREGULAR_DELAUNAY],
};

async function checkTriangularGrid(
  datasources: TSources | undefined
): Promise<boolean> {
  try {
    const gridsource = datasources!.levels[0].grid;
    await ZarrDataManager.getVariableInfo(
      gridsource,
      "vertex_of_cell",
      datasources?.zarr_format
    );
    return true;
  } catch {
    return false;
  }
}

function checkHealpixGrid(crs: zarr.Array<zarr.DataType, zarr.AsyncReadable>) {
  return crs.attrs["grid_mapping_name"] === "healpix";
}

function checkRegularRotatedGrid(
  crs: zarr.Array<zarr.DataType, zarr.AsyncReadable>
) {
  return crs.attrs["grid_mapping_name"] === "rotated_latitude_longitude";
}

function checkCurvilinear(
  latitudesVar: zarr.Array<zarr.DataType, zarr.AsyncReadable>,
  longitudesVar: zarr.Array<zarr.DataType, zarr.AsyncReadable>
) {
  return latitudesVar.shape.length === 2 && longitudesVar.shape.length === 2;
}

function checkGaussianGrid(latitudes: Float64Array, longitudes: Float64Array) {
  // Quick O(1) check: a Gaussian-reduced grid stores all cells for a given
  // latitude row consecutively, so the first two entries share the same lat.
  // If they differ, this is definitely not a Gaussian-reduced grid.
  if (latitudes.length < 2 || latitudes[0] !== latitudes[1]) {
    return false;
  }
  const uniqueLatsNum = new Set(latitudes).size;
  const uniqueLonsNum = new Set(longitudes).size;

  return (
    uniqueLatsNum * uniqueLonsNum !== latitudes.length * longitudes.length &&
    latitudes[0] === latitudes[1]
  );
}

// Check if grid is regular based on dimension names
// Also accepts lat-only grids (e.g., zonally averaged data)
function checkRegularGridFromDimensions(dimensions: string[]): boolean {
  const latitudeIndex = dimensions.findIndex((dim) => isLatitudeName(dim));
  const longitudeIndex = dimensions.findIndex((dim) => isLongitudeName(dim));
  const hasLatLon = latitudeIndex !== -1 && longitudeIndex !== -1;
  const hasLatOnly = latitudeIndex !== -1 && longitudeIndex === -1;
  return hasLatLon || hasLatOnly;
}

// Check if grid uses projected x/y coordinates (e.g. EPSG:3857 with spatial_ref)
function checkXYGridFromDimensions(dimensions: string[]): boolean {
  return dimensions.some(isXName) && dimensions.some(isYName);
}

// Attempt to determine grid type from CRS information
async function determineGridTypeFromCRS(
  datasources: TSources,
  varnameSelector: string
): Promise<T_GRID_TYPES | null> {
  try {
    const crs = await ZarrDataManager.getCRSInfo(datasources, varnameSelector);

    if (checkHealpixGrid(crs)) {
      return GRID_TYPES.HEALPIX;
    }
    if (checkRegularRotatedGrid(crs)) {
      return GRID_TYPES.REGULAR_ROTATED;
    }
    // Polar stereographic datasets are routed to CURVILINEAR so that
    // computePolarStereoLatLon2D can produce proper 2-D lat/lon arrays.
    if (crs.attrs?.grid_mapping_name === "polar_stereographic") {
      return GRID_TYPES.CURVILINEAR;
    }
  } catch {
    // CRS check failed, try full CRS string lookup below.
  }

  // Also check via the full CRS string lookup which handles PROJ4 fallbacks
  // written by rioxarray (group-level proj4_params attribute).
  try {
    const crsStr = await getCRSStringForXYVariable(
      datasources,
      varnameSelector
    );
    if (isPolarStereographicCRS(crsStr)) {
      return GRID_TYPES.CURVILINEAR;
    }
  } catch {
    // No CRS info available.
  }

  return null;
}

// Determine grid type from lat/lon data analysis
async function determineGridTypeFromData(
  datavar: zarr.Array<zarr.DataType, zarr.AsyncReadable>,
  datasources: TSources | undefined,
  varnameSelector: string
): Promise<T_GRID_TYPES | null> {
  if (!datasources) {
    return null;
  }
  // Fetch metadata only — no chunk data downloaded at this stage.
  // This avoids potentially hundreds of HTTP range-requests for large
  // curvilinear lat/lon arrays (e.g. 362×360 or 830 K-cell grids).
  let latitudesVar: zarr.Array<zarr.DataType, zarr.AsyncReadable> | null = null;
  let longitudesVar: zarr.Array<zarr.DataType, zarr.AsyncReadable> | null =
    null;
  try {
    ({ latitudesVar, longitudesVar } = await getLatLonVariableInfo(
      datavar,
      datasources,
      varnameSelector
    ));
  } catch {
    return null;
  }
  if (latitudesVar === null || longitudesVar === null) {
    return null; // Cannot determine grid type without both lat and lon
  }

  // Curvilinear grids have 2-D lat/lon arrays — detectable from shape alone.
  if (checkCurvilinear(latitudesVar, longitudesVar)) {
    return GRID_TYPES.CURVILINEAR;
  }

  // For Gaussian-reduced vs. irregular we need actual coordinate values.
  // Fetch both arrays in parallel to minimise wall-clock time.
  const [latitudes, longitudes] = await Promise.all([
    ZarrDataManager.getVariableDataFromArray(latitudesVar),
    ZarrDataManager.getVariableDataFromArray(longitudesVar),
  ]);
  const latitudesData = latitudes.data as Float64Array;
  const longitudesData = longitudes.data as Float64Array;

  if (checkGaussianGrid(latitudesData, longitudesData)) {
    return GRID_TYPES.GAUSSIAN_REDUCED;
  }
  // as long as we have lat/lon pairs, we can very likely display something as
  // an irregular grid
  return GRID_TYPES.IRREGULAR;
}

async function detectVariableGridType(
  datasources: TSources,
  varnameSelector: string
): Promise<T_GRID_TYPES> {
  const datavar = await ZarrDataManager.getVariableInfo(
    ZarrDataManager.getDatasetSource(datasources, varnameSelector),
    varnameSelector,
    datasources?.zarr_format
  );

  // Fetch dimension names first — for indexed datasets this is a fast
  // in-memory lookup (dimensionNames are cached in source.attrs).
  const dimensions = await ZarrDataManager.getDimensionNames(
    datasources,
    varnameSelector
  );

  // Fast path: plain lat/lon dimensions unambiguously identify a regular
  // grid.  Rotated grids use rlat/rlon instead and still need CRS detection
  // to return REGULAR_ROTATED.  Skip the expensive CRS network requests for
  // the common non-rotated case.
  const hasRotatedDims = dimensions.some((d) => d === "rlat" || d === "rlon");
  if (!hasRotatedDims && checkRegularGridFromDimensions(dimensions)) {
    return GRID_TYPES.REGULAR;
  }

  // Check CRS-based grid types (needed for rotated, healpix, projected grids)
  const crsGridType = await determineGridTypeFromCRS(
    datasources,
    varnameSelector
  );
  if (crsGridType) {
    return crsGridType;
  }

  if (checkRegularGridFromDimensions(dimensions)) {
    return GRID_TYPES.REGULAR;
  }

  const dataGridType = await determineGridTypeFromData(
    datavar,
    datasources,
    varnameSelector
  );
  if (dataGridType) {
    return dataGridType;
  }

  // Projected xy grids (e.g. EPSG:3857 with spatial_ref): handled as
  // regular grids after converting x/y coordinates to lat/lon.
  // Kept as a fallback so curvilinear datasets with x/y dimensions and
  // explicit 2-D lat/lon coordinates are not misclassified.
  if (checkXYGridFromDimensions(dimensions)) {
    return GRID_TYPES.REGULAR;
  }
  return GRID_TYPES.ERROR;
}

export async function getGridType(
  sourceValid: boolean,
  varnameSelector: string,
  datasources: TSources | undefined,
  logError: (maybeError: unknown, context?: string) => void
): Promise<T_GRID_TYPES> {
  // FIXME: This is a clumsy hack to distinguish between different
  // grid types.
  if (!sourceValid) {
    return GRID_TYPES.ERROR;
  }

  if (await checkTriangularGrid(datasources)) {
    return GRID_TYPES.TRIANGULAR;
  }

  try {
    return await detectVariableGridType(datasources!, varnameSelector);
  } catch (error) {
    logError(error, "Could not determine grid type");
    return GRID_TYPES.ERROR;
  }
}
